import * as React from 'react';
import { Button, Checkbox, DatePicker, Input, Modal, Select, Switch, Tag, notification } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useCampaignQuery, useCampaignRoster, useCreateReport } from '../queryHooks';
import { audienceTier, creatorCountry, STAGE_OPTIONS, totalAudience } from '../format';
import type { CampaignCreator, CampaignCreatorMeta } from '../types';

const TEAL = '#1f6f64';

const STEPS = [
    { title: 'Report Info', hint: 'Name your report and choose the desired timeframe.' },
    { title: 'Define Keyword Sets', hint: 'Define one or more keyword sets to track mentions of your brand, competitors, hashtags and more. Enter up to 500 keywords per set.' },
    { title: 'Define Influencer Segment', hint: 'Click customize to refine the influencer segment for your report.' },
    { title: 'Manage Report Options', hint: 'Choose up to 7 metrics to include in your report. Generate a shareable link to provide view-only access to this report.' },
];

const STEP_LABELS = ['REPORT INFO', 'KEYWORDS', 'INFLUENCERS', 'OPTIONS'];

type KeywordSet = { label: string; keywords: string };
type MetricKey = 'engagements' | 'video_views' | 'reach' | 'impressions' | 'est_impressions' | 'potential_reach' | 'engagement_rate' | 'vit' | 'cost';

const METRICS: { key: MetricKey; label: string; hint?: string }[] = [
    { key: 'engagements', label: 'Engagements' },
    { key: 'video_views', label: 'Video Views' },
    { key: 'reach', label: 'Reach', hint: 'Unique accounts that saw the posts.' },
    { key: 'impressions', label: 'Impressions', hint: 'Times the posts were shown.' },
    { key: 'est_impressions', label: 'Est Impressions', hint: 'Impression count, or follower total when impressions are missing.' },
    { key: 'potential_reach', label: 'Potential Reach' },
    { key: 'engagement_rate', label: 'Engagement Rate' },
    { key: 'vit', label: 'Brand Vitality Score (VIT)' },
    { key: 'cost', label: 'Cost per Metric' },
];

const DEFAULT_METRICS: Record<MetricKey, boolean> = {
    engagements: true,
    video_views: true,
    reach: false,
    impressions: false,
    est_impressions: false,
    potential_reach: false,
    engagement_rate: true,
    vit: false,
    cost: false,
};

type Filters = {
    locations: string[];
    tags: string[];
    tiers: string[];
    stages: string[];
    groups: string[];
    owners: string[];
    platforms: string[];
};

const EMPTY_FILTERS: Filters = {
    locations: [], tags: [], tiers: [], stages: [], groups: [], owners: [], platforms: [],
};

const ReportWizard: React.FC<{
    open: boolean;
    campaignId?: number;
    onCancel: () => void;
    onCreated: (reportId: number) => void;
}> = ({ open, campaignId, onCancel, onCreated }) => {
    const { data: campaign } = useCampaignQuery(open ? campaignId : undefined);
    const { data: roster } = useCampaignRoster(open ? campaignId : undefined);
    const create = useCreateReport();
    const [step, setStep] = React.useState(0);
    const [name, setName] = React.useState('');
    const [nameTouched, setNameTouched] = React.useState(false);
    const [start, setStart] = React.useState<Dayjs | null>(null);
    const [end, setEnd] = React.useState<Dayjs | null>(null);
    const [sets, setSets] = React.useState<KeywordSet[]>([{ label: '', keywords: '' }]);
    const [advanced, setAdvanced] = React.useState(false);
    const [customize, setCustomize] = React.useState(false);
    const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
    const [metrics, setMetrics] = React.useState(DEFAULT_METRICS);
    const [shareable, setShareable] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
    const [editingCreators, setEditingCreators] = React.useState(false);
    const [creatorQuery, setCreatorQuery] = React.useState('');
    const seeded = React.useRef(false);
    const creatorsSeeded = React.useRef(false);

    React.useEffect(() => {
        if (!open) {
            seeded.current = false;
            creatorsSeeded.current = false;
            setSelectedIds([]);
            return;
        }
        if (seeded.current) return;
        if (campaignId && campaign === undefined) return;
        seeded.current = true;
        setStep(0);
        setName('');
        setNameTouched(false);
        setStart(null);
        setEnd(null);
        const tags = (campaign?.hashtags ?? []).map((h) => h.tag).filter(Boolean);
        setSets([{ label: '', keywords: tags.join(', ') }]);
        setAdvanced(false);
        setCustomize(false);
        setFilters(EMPTY_FILTERS);
        setMetrics(DEFAULT_METRICS);
        setShareable(false);
        setEditingCreators(false);
        setCreatorQuery('');
    }, [open, campaign, campaignId]);

    React.useEffect(() => {
        if (!open || creatorsSeeded.current || !roster) return;
        creatorsSeeded.current = true;
        setSelectedIds(roster.creators
            .filter((c) => metaOf(roster.creator_metas, c.id)?.approved === true)
            .map((c) => c.id));
    }, [open, roster]);

    const creators = roster?.creators ?? [];
    const metas = roster?.creator_metas ?? [];
    const selectedIdSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
    const selected = creators.filter((c) => selectedIdSet.has(c.id));
    const creatorChoices = creators
        .filter((c) => creatorMatches(c, metaOf(metas, c.id), filters, customize))
        .filter((c) => !creatorQuery.trim() || (c.creator || '').toLowerCase().includes(creatorQuery.trim().toLowerCase()))
        .sort((a, b) => (a.creator || '').localeCompare(b.creator || ''));
    const nameMissing = !name.trim();
    const metricCount = Object.values(metrics).filter(Boolean).length;

    const toggleMetric = (key: MetricKey, on: boolean) => {
        if (on && metricCount >= 7) return;
        setMetrics((prev) => ({ ...prev, [key]: on }));
    };

    const save = async () => {
        if (!campaignId || nameMissing) return;
        try {
            const created = await create.mutateAsync({
                campaignId,
                name: name.trim(),
                start_date: start ? start.startOf('day').toISOString() : campaign?.start_date,
                end_date: end ? end.endOf('day').toISOString() : campaign?.end_date,
            });
            if (created.id) onCreated(created.id);
        } catch {
            notification.error({ message: 'Failed to create report' });
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            width={860}
            onCancel={onCancel}
            destroyOnClose
            styles={{ body: { padding: '28px 40px 20px' } }}
        >
            <Stepper step={step} />
            <div style={{ color: '#bfbfbf', marginTop: 28 }}>Step {step + 1}</div>
            <h2 style={{ margin: '4px 0 8px', fontSize: 28, fontWeight: 600 }}>{STEPS[step].title}</h2>
            <p style={{ color: '#595959', marginTop: 0, maxWidth: 640 }}>{STEPS[step].hint}</p>

            {step === 0 && (
                <div>
                    <Field label="Report Name">
                        <Input
                            value={name}
                            placeholder="Name your report"
                            status={nameTouched && nameMissing ? 'error' : undefined}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => setNameTouched(true)}
                        />
                        {nameTouched && nameMissing && (
                            <div style={{ color: '#cf1322', marginTop: 4 }}>Please enter a name for your report.</div>
                        )}
                    </Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <Field label="Start Date">
                            <DatePicker value={start} onChange={setStart} placeholder="Select start date" style={{ width: '100%' }} />
                        </Field>
                        <Field label="End Date">
                            <DatePicker value={end} onChange={setEnd} placeholder="Select end date" style={{ width: '100%' }} />
                        </Field>
                    </div>
                </div>
            )}

            {step === 1 && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <Switch checked={advanced} onChange={setAdvanced} checkedChildren="On" unCheckedChildren="Off" className="report-switch" />
                        <span>Advanced Mode</span>
                    </div>
                    {sets.map((set, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, marginBottom: 12 }}>
                            <Field label={i === 0 ? 'Label' : ''}>
                                <Input
                                    value={set.label}
                                    placeholder="Label"
                                    onChange={(e) => updateSet(setSets, i, { label: e.target.value })}
                                />
                            </Field>
                            <Field label={i === 0 ? 'Keywords' : ''}>
                                {advanced ? (
                                    <Input.TextArea
                                        value={set.keywords}
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        placeholder="One keyword per line"
                                        onChange={(e) => updateSet(setSets, i, { keywords: e.target.value })}
                                    />
                                ) : (
                                    <Input
                                        value={set.keywords}
                                        placeholder="HBH, ήβη, Ηβη"
                                        onChange={(e) => updateSet(setSets, i, { keywords: e.target.value })}
                                    />
                                )}
                            </Field>
                        </div>
                    ))}
                    <Button onClick={() => setSets((prev) => [...prev, { label: '', keywords: '' }])}>Add new keyword set</Button>
                </div>
            )}

            {step === 2 && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                        <Button style={{ color: '#1677ff' }} onClick={() => setEditingCreators((openList) => !openList)}>
                            {selected.length} Influencers selected
                        </Button>
                        <Switch checked={customize} onChange={setCustomize} checkedChildren="On" unCheckedChildren="Off" className="report-switch" />
                        <span>Customize</span>
                    </div>
                    {editingCreators && (
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                            <Input.Search
                                allowClear
                                placeholder="Search creators"
                                value={creatorQuery}
                                onChange={(e) => setCreatorQuery(e.target.value)}
                                style={{ marginBottom: 8 }}
                            />
                            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                                {creatorChoices.length === 0 && (
                                    <div style={{ color: '#8c8c8c', padding: '8px 0' }}>No creators match.</div>
                                )}
                                {creatorChoices.map((c) => {
                                    const approved = metaOf(metas, c.id)?.approved === true;
                                    return (
                                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                                            <Checkbox
                                                checked={selectedIdSet.has(c.id)}
                                                onChange={(e) => {
                                                    const on = e.target.checked;
                                                    setSelectedIds((prev) => on ? [...prev, c.id] : prev.filter((id) => id !== c.id));
                                                }}
                                            />
                                            <span>{c.creator || `Creator #${c.id}`}</span>
                                            {approved && <Tag color="green" style={{ margin: 0 }}>Approved</Tag>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {customize && (
                        <FilterForm
                            creators={creators}
                            metas={metas}
                            filters={filters}
                            onChange={setFilters}
                        />
                    )}
                </div>
            )}

            {step === 3 && (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Manage Metrics</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px', marginBottom: 24 }}>
                        {METRICS.map((m) => (
                            <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Switch
                                    checked={metrics[m.key]}
                                    checkedChildren="On"
                                    unCheckedChildren="Off"
                                    className="report-switch"
                                    onChange={(on) => toggleMetric(m.key, on)}
                                />
                                <span>{m.label}</span>
                                {m.hint && <span title={m.hint} style={{ color: '#8c8c8c', cursor: 'help' }}>ⓘ</span>}
                            </label>
                        ))}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Manage Sharing</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Switch
                            checked={shareable}
                            onChange={setShareable}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            className="report-switch"
                        />
                        <span>Shareable Link</span>
                    </label>
                    {!shareable && (
                        <div style={{ color: '#cf1322', marginTop: 8 }}>Not available for unshared reports.</div>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 28 }}>
                <Button onClick={onCancel}>Cancel</Button>
                {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Prev</Button>}
                {step < 3 ? (
                    <Button
                        type="primary"
                        disabled={step === 0 && nameMissing}
                        onClick={() => {
                            if (step === 0 && nameMissing) {
                                setNameTouched(true);
                                return;
                            }
                            setStep((s) => s + 1);
                        }}
                        style={step === 0 && nameMissing ? undefined : { background: '#111', borderColor: '#111' }}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        loading={create.isLoading}
                        disabled={nameMissing}
                        onClick={save}
                        style={{ background: '#111', borderColor: '#111' }}
                    >
                        Save Report
                    </Button>
                )}
            </div>
            <style>{`
                .report-switch.ant-switch-checked { background: #8fd4b0 !important; }
                .report-switch:not(.ant-switch-checked) { background: #4a4a4a !important; }
            `}</style>
        </Modal>
    );
};

function Stepper({ step }: { step: number }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 0 }}>
            {STEP_LABELS.map((label, i) => (
                <React.Fragment key={label}>
                    {i > 0 && (
                        <div style={{ width: 48, height: 1, background: '#d9d9d9', marginTop: 16 }} />
                    )}
                    <div style={{ textAlign: 'center', width: 110 }}>
                        <div style={{
                            width: 32,
                            height: 32,
                            margin: '0 auto',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            border: `2px solid ${i <= step ? TEAL : '#d9d9d9'}`,
                            background: i === step ? TEAL : '#fff',
                            color: i === step ? '#fff' : i < step ? TEAL : '#bfbfbf',
                        }}>
                            {i < step ? <CheckOutlined /> : i + 1}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, letterSpacing: 0.4, color: '#8c8c8c' }}>{label}</div>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ marginBottom: 12 }}>
            {label && <div style={{ marginBottom: 6, fontWeight: 500 }}>{label}</div>}
            {children}
        </div>
    );
}

function updateSet(setSets: React.Dispatch<React.SetStateAction<KeywordSet[]>>, index: number, patch: Partial<KeywordSet>) {
    setSets((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
}

function metaOf(metas: CampaignCreatorMeta[], id: number) {
    return metas.find((m) => m.creator_id === id);
}

function creatorMatches(c: CampaignCreator, meta: CampaignCreatorMeta | undefined, filters: Filters, customize: boolean) {
    if (!customize) return true;
    if (filters.locations.length && !filters.locations.includes(creatorCountry(c))) return false;
    if (filters.tiers.length && !filters.tiers.includes(audienceTier(totalAudience(c)).toLowerCase())) return false;
    if (filters.stages.length && !filters.stages.includes(meta?.relationship_stage || '')) return false;
    if (filters.tags.length) {
        const tags = (c.tags ?? []).map((t) => t.TagCategoryValue?.value).filter(Boolean) as string[];
        if (!filters.tags.some((t) => tags.includes(t))) return false;
    }
    if (filters.platforms.length) {
        const ids = new Set((c.accounts ?? []).map((a) => a.platform_id));
        const names = new Set((c.accounts ?? []).map((a) => a.platform?.name));
        const hit = filters.platforms.some((p) =>
            (p === 'yt' && (ids.has(1) || names.has('YouTube')))
            || (p === 'ig' && (ids.has(2) || names.has('Instagram')))
            || (p === 'tt' && (ids.has(3) || names.has('TikTok'))),
        );
        if (!hit) return false;
    }
    return true;
}

function FilterForm({
    creators, metas, filters, onChange,
}: {
    creators: CampaignCreator[];
    metas: CampaignCreatorMeta[];
    filters: Filters;
    onChange: (next: Filters) => void;
}) {
    const locations = unique(creators.map(creatorCountry).filter(Boolean));
    const tags = unique(creators.flatMap((c) => (c.tags ?? []).map((t) => t.TagCategoryValue?.value || '')).filter(Boolean));
    const tiers = [
        { label: 'Nano', value: 'nano' },
        { label: 'Micro', value: 'micro' },
        { label: 'Mid', value: 'mid' },
        { label: 'Macro', value: 'macro' },
        { label: 'Mega', value: 'mega' },
    ];
    const set = (key: keyof Filters, value: string[]) => onChange({ ...filters, [key]: value });
    const row = (label: string, node: React.ReactNode) => (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div>{label}</div>
            {node}
        </div>
    );
    return (
        <div>
            {row('Locations', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Locations" value={filters.locations} options={locations.map((v) => ({ label: v, value: v }))} onChange={(v) => set('locations', v)} />)}
            {row('Tags', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Tags" value={filters.tags} options={tags.map((v) => ({ label: v, value: v }))} onChange={(v) => set('tags', v)} />)}
            {row('Tiers', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Tiers" value={filters.tiers} options={tiers} onChange={(v) => set('tiers', v)} />)}
            {row('Stages', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Relationship Stages" value={filters.stages} options={STAGE_OPTIONS} onChange={(v) => set('stages', v)} />)}
            {row('Groups', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Groups" value={filters.groups} options={[]} onChange={(v) => set('groups', v)} />)}
            {row('Owners', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Relationship Owners" value={filters.owners} options={[]} onChange={(v) => set('owners', v)} />)}
            {row('Platforms', <Select style={{ width: '100%' }} mode="multiple" allowClear placeholder="Select Platforms" value={filters.platforms} options={[{ label: 'YouTube', value: 'yt' }, { label: 'Instagram', value: 'ig' }, { label: 'TikTok', value: 'tt' }]} onChange={(v) => set('platforms', v)} />)}
        </div>
    );
}

function unique(values: string[]) {
    return [...new Set(values)];
}

export default ReportWizard;
